import { useMemo, useState } from 'react';
import { Box, Chip, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';
import type { ActionItem, NewsletterVersion } from '@/types';
import { ACTION_ITEM_STATUS_LABELS, SECTION_LABELS } from '@/types';
import { compareVersions, diffActionItems, type DiffPart } from '@/services/versionService';
import { formatDateTime } from '@/utils/dateUtils';
import { EmptyState } from '@/components/common/Common';

function DiffText({ parts }: { parts: DiffPart[] }) {
  return (
    <Typography component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
      {parts.map((part, index) => (
        <Box
          key={index}
          component="span"
          sx={{
            bgcolor: part.added ? 'success.light' : part.removed ? 'error.light' : 'transparent',
            textDecoration: part.removed ? 'line-through' : 'none',
            color: part.added || part.removed ? 'common.black' : 'text.primary',
            borderRadius: 0.5,
          }}
        >
          {part.value}
        </Box>
      ))}
    </Typography>
  );
}

export default function VersionHistoryView({
  versions,
  actionItemsById,
}: {
  versions: NewsletterVersion[];
  actionItemsById: Map<string, ActionItem>;
}) {
  const sorted = useMemo(() => [...versions].sort((a, b) => b.versionNumber - a.versionNumber), [versions]);
  const [compareId, setCompareId] = useState(sorted[0]?.id ?? '');
  const [baseId, setBaseId] = useState(sorted[1]?.id ?? sorted[0]?.id ?? '');

  if (sorted.length === 0) {
    return <EmptyState title="No versions yet" description="Publish this newsletter or save a version to start tracking history." />;
  }

  if (sorted.length === 1) {
    return (
      <EmptyState
        title="Only one version so far"
        description="Publish again or save another version to unlock comparison."
      />
    );
  }

  const base = sorted.find((v) => v.id === baseId) ?? sorted[1];
  const compare = sorted.find((v) => v.id === compareId) ?? sorted[0];
  const comparison = compareVersions(base, compare);
  const actionDiff = diffActionItems(base.snapshot.actionItemIds, compare.snapshot.actionItemIds, actionItemsById);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Base version
          </Typography>
          <Select size="small" value={base.id} onChange={(e) => setBaseId(e.target.value)} sx={{ minWidth: 220 }}>
            {sorted.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                v{v.versionNumber} · {formatDateTime(v.createdAt)}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Compare to
          </Typography>
          <Select size="small" value={compare.id} onChange={(e) => setCompareId(e.target.value)} sx={{ minWidth: 220 }}>
            {sorted.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                v{v.versionNumber} · {formatDateTime(v.createdAt)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Stack>

      {comparison.titleChanged && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h4" gutterBottom>
            Title
          </Typography>
          <DiffText parts={comparison.titleDiff} />
        </Paper>
      )}

      {comparison.sectionDiffs
        .filter((diff) => diff.changed)
        .map((diff) => (
          <Paper key={diff.section} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>
              {SECTION_LABELS[diff.section]}
            </Typography>
            <DiffText parts={diff.parts} />
          </Paper>
        ))}

      {comparison.sectionDiffs.every((d) => !d.changed) && !comparison.titleChanged && (
        <EmptyState title="No narrative changes" description="Sections are identical between these two versions." />
      )}

      {comparison.kpiDiffs.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h4" gutterBottom>
            Metrics &amp; KPIs
          </Typography>
          <Stack spacing={1}>
            {comparison.kpiDiffs.map((kpi) => (
              <Stack key={kpi.id} direction="row" spacing={1} alignItems="center">
                <Typography sx={{ minWidth: 160 }}>{kpi.label}</Typography>
                {kpi.status === 'added' && <Chip size="small" color="success" label={`Added: ${kpi.after?.value}`} />}
                {kpi.status === 'removed' && <Chip size="small" color="error" label={`Removed: ${kpi.before?.value}`} />}
                {kpi.status === 'changed' && (
                  <Chip size="small" color="warning" label={`${kpi.before?.value} → ${kpi.after?.value}`} />
                )}
                {kpi.status === 'unchanged' && (
                  <Chip size="small" variant="outlined" label={kpi.after?.value ?? kpi.before?.value} />
                )}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {actionDiff.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h4" gutterBottom>
            Action Items
          </Typography>
          <Stack spacing={1}>
            {actionDiff.map((entry) => {
              const item = actionItemsById.get(entry.id);
              if (!item) return null;
              const label =
                entry.status === 'added'
                  ? 'New'
                  : entry.status === 'removed'
                  ? 'Removed'
                  : entry.status === 'completed_since'
                  ? `Completed (${ACTION_ITEM_STATUS_LABELS.completed})`
                  : 'Unchanged';
              const color =
                entry.status === 'added'
                  ? 'success'
                  : entry.status === 'removed'
                  ? 'error'
                  : entry.status === 'completed_since'
                  ? 'success'
                  : 'default';
              return (
                <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
                  <Chip size="small" color={color} label={label} />
                  <Typography>{item.title}</Typography>
                </Stack>
              );
            })}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
